// src/components/SensorPage/useSensorData.js

import { useState, useEffect, useCallback, useRef } from 'react';

// ✅ Configurado para buscar dados do InfluxDB
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3057/api';

// Removido: dados mockados não são mais usados - apenas dados reais do sensor
// Se não houver dados disponíveis, a API retornará null/undefined e será tratado adequadamente

// Funções auxiliares para cálculos
// Cálculo completo de PMV conforme ISO 7730
function calculatePMVFromData(data) {
  // Valores fixos conforme solicitado
  const CLO = 0.5;  // Isolamento térmico da roupa
  const MET = 1.2;  // Taxa metabólica
  
  // Verificar se temos dados mínimos necessários
  if (!data || !data.temperature || data.temperature === null || 
      !data.humidity || data.humidity === null) {
    return null;
  }
  
  // Parâmetros ambientais
  const ta = data.temperature;  // Temperatura do ar (bulbo seco) (°C)
  const rh = data.humidity;       // Umidade relativa (%)
  
  // Temperatura radiante média (tr)
  // Se não disponível, usar temperatura operativa aproximada:
  // Top = (ta + tr) / 2 ≈ ta quando tr ≈ ta (ambiente bem misturado)
  // Para maior precisão, pode-se usar: tr = ta + offset (geralmente 0-2°C)
  // Por enquanto, usando ta como aproximação (assumindo tr ≈ ta)
  const tr = data.temperature || ta;  // Temperatura radiante média (°C) - usando ta como aproximação
  
  // Velocidade do ar (va)
  // Padrão ISO 7730 para ambiente interno sem movimento significativo: 0.1 m/s
  // Valores típicos:
  // - Ambiente estacionário: 0.05-0.1 m/s
  // - Ambiente com ventilação natural: 0.1-0.2 m/s
  // - Ambiente com ar condicionado: 0.1-0.3 m/s
  // - Ambiente com ventilação forçada: 0.2-0.5 m/s
  const va = 0.1;  // Velocidade do ar (m/s) - padrão ISO 7730 para ambiente interno
  
  // Conversões e constantes
  const M = MET * 58.15;  // Taxa metabólica em W/m² (1 MET = 58.15 W/m²)
  const W = 0;  // Trabalho externo (W/m²) - geralmente 0 para atividades sedentárias
  const Icl = CLO * 0.155;  // Isolamento térmico em m²·K/W (1 CLO = 0.155 m²·K/W)
  
  // Calcular pressão parcial do vapor de água (pa) em Pascal (Pa)
  // Usando umidade relativa (RH) e fórmula de Magnus-Tetens
  // A umidade relativa é a razão entre a pressão parcial do vapor e a pressão de saturação
  // pa = (RH/100) * psat(ta)
  // Onde psat é calculada usando a fórmula de Magnus-Tetens
  const a = 17.27;
  const b = 237.7;
  const alpha = (a * ta) / (b + ta) + Math.log(rh / 100.0);
  const tDew = (b * alpha) / (a - alpha);  // Temperatura do ponto de orvalho (°C)
  const pa = 611.2 * Math.exp((17.67 * tDew) / (tDew + 243.5));  // Pressão parcial do vapor (Pa)
  
  // Calcular fator de área de roupa (fcl)
  let fcl;
  if (Icl <= 0.078) {
    fcl = 1.0 + 1.29 * Icl;
  } else {
    fcl = 1.05 + 0.645 * Icl;
  }
  
  // Calcular temperatura da superfície da roupa (tcl) - iteração necessária
  let tcl = ta + (35.5 - ta) / (3.5 * (Icl + 0.1));
  let hc;  // Coeficiente de transferência de calor por convecção
  
  // Iterar para encontrar tcl e hc
  for (let i = 0; i < 20; i++) {
    // Calcular hc baseado na diferença de temperatura
    const tclDiff = Math.abs(tcl - ta);
    if (tclDiff > 0.1) {
      hc = 2.38 * Math.pow(tclDiff, 0.25);
    } else {
      hc = 12.1 * Math.sqrt(va);
    }
    
    // Atualizar tcl
    const tclNew = 35.7 - 0.028 * (M - W) - Icl * fcl * (3.96 * Math.pow(10, -8) * fcl * 
                   (Math.pow(tcl + 273, 4) - Math.pow(tr + 273, 4)) + hc * (tcl - ta));
    
    // Verificar convergência
    if (Math.abs(tclNew - tcl) < 0.001) {
      tcl = tclNew;
      break;
    }
    tcl = tclNew;
  }
  
  // Calcular PMV usando fórmula completa ISO 7730
  const pmv = (0.303 * Math.exp(-0.036 * M) + 0.028) * 
              ((M - W) - 
               3.05 * Math.pow(10, -3) * (5733 - 6.99 * (M - W) - pa) - 
               0.42 * ((M - W) - 58.15) - 
               1.7 * Math.pow(10, -5) * M * (5867 - pa) - 
               0.0014 * M * (34 - ta) - 
               3.96 * Math.pow(10, -8) * fcl * (Math.pow(tcl + 273, 4) - Math.pow(tr + 273, 4)) - 
               fcl * hc * (tcl - ta));
  
  // Limitar PMV entre -3 e +3
  return parseFloat(Math.max(-3, Math.min(3, pmv)).toFixed(2));
}

function calculatePPDFromPMV(pmv) {
  if (pmv === null) return null;
  
  // Fórmula ISO 7730 para PPD
  const ppd = 100 - 95 * Math.exp(-0.03353 * Math.pow(pmv, 4) - 0.2179 * Math.pow(pmv, 2));
  return parseFloat(Math.max(5, Math.min(100, ppd)).toFixed(2));
}

function calculateDewpoint(temp, humidity) {
  // Cálculo simplificado do ponto de orvalho
  const a = 17.27;
  const b = 237.7;
  const alpha = ((a * temp) / (b + temp)) + Math.log(humidity / 100.0);
  const dewpoint = (b * alpha) / (a - alpha);
  return parseFloat(dewpoint.toFixed(2));
}

function calculateEnthalpy(temp, humidity) {
  // Cálculo simplificado da entalpia (kJ/kg)
  const enthalpy = 1.006 * temp + (humidity / 100) * (2501 + 1.86 * temp);
  return parseFloat(enthalpy.toFixed(2));
}

function calculateWetbulb(temp, humidity) {
  // Cálculo simplificado da temperatura de bulbo úmido
  const wetbulb = temp * Math.atan(0.151977 * Math.sqrt(humidity + 8.313659)) +
                   Math.atan(temp + humidity) -
                   Math.atan(humidity - 1.676331) +
                   0.00391838 * Math.pow(humidity, 1.5) * Math.atan(0.023101 * humidity) -
                   4.686035;
  return parseFloat(Math.max(temp - 5, Math.min(temp, wetbulb)).toFixed(2));
}

function formatTimeForChart(timestamp, timeRange) {
  const date = new Date(timestamp);
  
  switch(timeRange) {
    case '1h':
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    case '24h':
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    case '7d':
      return date.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
    case '30d':
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    default:
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
}

export const useSensorData = (sensorId, timeRange = '24h') => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      
      // Removido: lógica de dados mockados - apenas dados reais são usados
      // Se não houver dados disponíveis, a API retornará null/undefined e será tratado adequadamente
      
      // Determinar intervalo baseado no timeRange
      const intervalMap = {
        '1h': '5m',
        '24h': '15m',
        '7d': '1h',
        '30d': '3h',
        '6m': '6h',   // 6 meses - intervalo de 6 horas
        '1y': '12h',  // 1 ano - intervalo de 12 horas
        'all': '1d'   // Tudo - intervalo de 1 dia
      };
      const interval = intervalMap[timeRange] || '15m';
      
      // Buscar dados do dashboard (instantâneos + históricos) para outros sensores
      const response = await fetch(
        `${API_BASE_URL}/sensors/${sensorId}/dashboard?timeRange=${timeRange}&interval=${interval}`,
        {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        const apiData = result.data;
        
        // Processar dados para o formato esperado pelos componentes
        const processedData = {
          sensorId: apiData.sensorId,
          timestamp: apiData.timestamp,
          location: apiData.location,
          current: {
            // Valores diretos do InfluxDB ou null se não disponível
            temperature: apiData.current.temperature,
            humidity: apiData.current.humidity,
            co2: apiData.current.co2,
            pm25: apiData.current.pm25,
            pm10: apiData.current.pm10,
            pm1: apiData.current.pm1,
            voc: apiData.current.voc,
            dewpoint: apiData.current.dewpoint,
            enthalpy: apiData.current.enthalpy,
            wetbulb: apiData.current.wetbulb,
            absoluteHumidity: apiData.current.absoluteHumidity,
            specificHumidity: apiData.current.specificHumidity,
            luminosity: apiData.current.luminosity,
            noise: apiData.current.noise,
            // Calcular PMV/PPD se tiver dados suficientes
            pmv: calculatePMVFromData(apiData.current),
            ppd: calculatePMVFromData(apiData.current) !== null ? calculatePPDFromPMV(calculatePMVFromData(apiData.current)) : null
          },
          strategic: {
            // Usar dados do backend quando disponíveis, caso contrário calcular localmente
            comfort: apiData.strategic?.comfort || generateComfortMetrics(apiData.current),
            airQuality: apiData.strategic?.airQuality || generateAirQualityMetrics(apiData.current),
            cognition: apiData.strategic?.cognition || { index: null, impact: "Análise em desenvolvimento" },
            health: apiData.strategic?.health || { index: null, factors: [] },
            efficiency: apiData.strategic?.efficiency || { deltaHumidity: null, status: "Análise em desenvolvimento", estimatedSavings: null },
            ventilation: apiData.strategic?.ventilation || { co2Accumulation: null, recommendation: "Análise em desenvolvimento" }
          },
          technical: {
            hvacRuntime: {
              isActive: false,
              last24h: { active: 0, inactive: 24 },
              optimal: { start: "07:00", stop: "18:00" }
            },
            setpoint: {
              current: 24.0,
              recommended: 23.8,
              reason: "Análise em desenvolvimento",
              adjustment: -0.2
            }
          },
          historical: apiData.historical || [],
          availableReadings: apiData.availableReadings || {}, // Adicionar leituras disponíveis
          hasData: apiData.hasData
        };
        
        setData(processedData);
      } else {
        throw new Error(result.error || 'Erro ao buscar dados');
      }
    } catch (err) {
      console.error('Erro ao buscar dados do sensor:', err);
      setError(err.message);
      
      // Se não houver dados, mostrar estrutura vazia
      setData({
        sensorId,
        timestamp: new Date().toISOString(),
        location: { name: `Sensor ${sensorId}`, address: 'Endereço não disponível' },
        current: {
          temperature: null,
          humidity: null,
          co2: null,
          pm25: null,
          pm10: null,
          voc: null,
          dewpoint: null,
          enthalpy: null,
          wetbulb: null,
          pmv: null,
          ppd: null
        },
        strategic: {
          comfort: { pmv: null, ppd: null, classification: "Sem dados", score: null },
          airQuality: { index: null, classification: "Sem dados" },
          cognition: { index: null, impact: "Sem dados" },
          health: { index: null, factors: [] },
          efficiency: { cop: null, status: "Sem dados" },
          ventilation: { airChangesPerHour: null, recommendation: "Sem dados" }
        },
        technical: {
          hvacRuntime: {
            isActive: false,
            last24h: { active: 0, inactive: 24 },
            optimal: { start: "07:00", stop: "18:00" }
          },
          setpoint: {
            current: null,
            recommended: null,
            reason: "Sem dados disponíveis",
            adjustment: 0
          }
        },
        historical: [],
        availableReadings: {}, // Adicionar availableReadings vazio no fallback
        hasData: false
      });
    } finally {
      setLoading(false);
    }
  }, [sensorId, timeRange]);

  // Funções auxiliares para cálculos básicos
  const generateComfortMetrics = (data) => {
    const pmv = calculatePMVFromData(data);
    const ppd = pmv !== null ? calculatePPDFromPMV(pmv) : null;
    
    if (pmv === null || ppd === null) {
      return {
        pmv: null,
        ppd: null,
        classification: "Sem dados",
        score: null,
        clo: 0.5,
        met: 1.2
      };
    }
    
    return {
      pmv,
      ppd,
      classification: pmv < -0.5 ? "Frio" :
                     pmv < 0.5 ? "Confortável" :
                     "Quente",
      score: Math.max(0, 100 - Math.abs(pmv) * 30 - ppd * 0.5),
      clo: 0.5,
      met: 1.2
    };
  };

  const generateAirQualityMetrics = (data) => {
    if (!data.co2 && !data.pm25 && !data.voc) {
      return {
        index: null,
        co2: null,
        voc: null,
        pm25: null,
        classification: "Sem dados"
      };
    }
    
    // Função para calcular score individual baseado nos limites de referência
    const calculateCO2Score = (co2) => {
      if (co2 == null || isNaN(co2)) return null;
      if (co2 < 700) return 100; // Excelente
      if (co2 < 1000) {
        // Interpolação linear: 700=100, 1000=85
        return 100 - ((co2 - 700) / 300) * 15;
      }
      if (co2 < 1500) {
        // Interpolação linear: 1000=85, 1500=60
        return 85 - ((co2 - 1000) / 500) * 25;
      }
      if (co2 < 2000) {
        // Interpolação linear: 1500=60, 2000=30
        return 60 - ((co2 - 1500) / 500) * 30;
      }
      // Muito Crítico: interpolação linear de 2000=30 até valores muito altos
      return Math.max(10, 30 - ((co2 - 2000) / 1000) * 20);
    };
    
    const calculateVOCScore = (voc) => {
      if (voc == null || isNaN(voc)) return null;
      if (voc < 200) return 100; // Excelente
      if (voc < 400) {
        // Interpolação linear: 200=100, 400=85
        return 100 - ((voc - 200) / 200) * 15;
      }
      if (voc < 1000) {
        // Interpolação linear: 400=85, 1000=60
        return 85 - ((voc - 400) / 600) * 25;
      }
      // Crítico: interpolação linear de 1000=60 até valores muito altos
      return Math.max(20, 60 - ((voc - 1000) / 500) * 40);
    };
    
    const calculatePM25Score = (pm25) => {
      if (pm25 == null || isNaN(pm25)) return null;
      if (pm25 < 5) return 100; // Excelente
      if (pm25 < 12) {
        // Interpolação linear: 5=100, 12=85
        return 100 - ((pm25 - 5) / 7) * 15;
      }
      if (pm25 < 25) {
        // Interpolação linear: 12=85, 25=60
        return 85 - ((pm25 - 12) / 13) * 25;
      }
      if (pm25 < 35) {
        // Interpolação linear: 25=60, 35=30
        return 60 - ((pm25 - 25) / 10) * 30;
      }
      // Muito Ruim: interpolação linear de 35=30 até valores muito altos
      return Math.max(10, 30 - ((pm25 - 35) / 20) * 20);
    };
    
    // Calcular scores individuais
    const co2Score = calculateCO2Score(data.co2);
    const vocScore = calculateVOCScore(data.voc);
    const pm25Score = calculatePM25Score(data.pm25);
    
    // Calcular média ponderada (cada métrica contribui igualmente se disponível)
    const scores = [co2Score, vocScore, pm25Score].filter(s => s !== null);
    const index = scores.length > 0 
      ? scores.reduce((sum, score) => sum + score, 0) / scores.length
      : null;
    
    return {
      index: index != null ? Math.round(index * 100) / 100 : null,
      co2: data.co2,
      voc: data.voc,
      pm25: data.pm25,
      classification: index == null ? "Sem dados" :
                     index >= 80 ? "Excelente" :
                     index >= 60 ? "Bom" :
                     index >= 40 ? "Moderado" :
                     "Ruim"
    };
  };

  useEffect(() => {
    console.log(`🔄 useEffect executado - sensorId: ${sensorId}, timeRange: ${timeRange}`);
    setLoading(true);
    fetchData();
    
    // Limpar intervalo anterior se existir
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    // Atualizar a cada 30 segundos
    intervalRef.current = setInterval(() => {
      console.log('🔄 Atualização automática (30s)');
      fetchData();
    }, 30000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [sensorId, timeRange, fetchData]);

  return { data, loading, error, refetch: fetchData };
};